// Sections 8, 13, 15 and 18: who may do which job. Used by validateDecision, the steward,
// default assignments and production (an ineligible worker produces nothing).
import { hasBuilding, isChild, isElder } from '../../core/queries';
import type { JobId, Person, ReadContext } from '../../core/types';

export type WorkBlock =
  | 'too-young'
  | 'child-labour'
  | 'elder-heavy'
  | 'needs-building'
  | 'too-weak'
  | 'trait'
  | 'job-rule'
  // Section 18 compulsory schooling keeps children of that age out of work while a school stands.
  | 'schooling';

/** The last completed season's turn, whether called between seasons or during one. */
export function lastCompletedTurn(ctx: ReadContext): number {
  return ctx.state.currentReport === null ? ctx.state.turn : ctx.state.turn - 1;
}

export function workBlock(ctx: ReadContext, person: Person, jobId: JobId): WorkBlock | null {
  if (jobId === 'rest') return null;
  const { cfg, registry, state } = ctx;
  const def = registry.jobs[jobId];
  if (person.ageSeasons < cfg.work.childWorkFromYears * cfg.people.seasonsPerYear)
    return 'too-young';
  if (isChild(cfg, person)) {
    const policy = state.policies.childLabour;
    const emergency =
      state.counters.lastShortageTurn !== null &&
      state.counters.lastShortageTurn >= lastCompletedTurn(ctx);
    if (policy === 'never' || (policy === 'emergencies' && !emergency)) return 'child-labour';
    const compulsoryTo = cfg.skills.compulsoryToYears[state.policies.schooling];
    if (
      compulsoryTo !== null &&
      person.ageSeasons < (compulsoryTo + 1) * cfg.people.seasonsPerYear &&
      hasBuilding(state, 'school')
    )
      return 'schooling';
  }
  if (def.heavy && isElder(cfg, person)) return 'elder-heavy';
  if (def.requiresBuilding !== null && !hasBuilding(state, def.requiresBuilding))
    return 'needs-building';
  if (person.strength < def.minStrength) return 'too-weak';
  for (const trait of person.traits)
    if (registry.traits[trait].restricts(ctx, person, jobId)) return 'trait';
  if (!def.eligible(ctx, person)) return 'job-rule';
  return null;
}

export function canWork(ctx: ReadContext, person: Person, jobId: JobId): boolean {
  return workBlock(ctx, person, jobId) === null;
}
